/** @format */

import { styled, keyframes } from "@mui/material/styles";
import { Typography, Box } from "@mui/material";
import { colors } from "../../utility/types/constant";

// Keyframe animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const floatRandom = keyframes`
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  33% {
    transform: translate(30px, -30px) rotate(120deg);
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(30, 193, 149, 0.4);
  }
  50% {
    box-shadow: 0 0 40px rgba(30, 193, 149, 0.8);
  }
`;

const badgePulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(30, 193, 149, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(30, 193, 149, 0);
  }
`;

export const Container = styled("div")(({ theme }) => ({
	position: "relative",
	padding: "80px 20px",
	background: `linear-gradient(135deg, ${colors.primary} 0%, #051442 50%, ${colors.thirdPrimary} 100%)`,
	overflow: "hidden",
	marginTop: "60px",
	marginBottom: "60px",

	[theme.breakpoints.down("sm")]: {
		padding: "60px 15px",
	},
}));

export const GradientOverlay = styled("div")({
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	background:
		"radial-gradient(circle at 20% 50%, rgba(30, 193, 149, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(30, 193, 149, 0.15) 0%, transparent 50%)",
	pointerEvents: "none",
	zIndex: 1,
});

export const FloatingElement = styled(Box)(({ theme }) => ({
	position: "absolute",
	width: "100px",
	height: "100px",
	borderRadius: "50%",
	background: `radial-gradient(circle, rgba(30, 193, 149, 0.2), transparent)`,
	animation: `${floatRandom} 20s ease-in-out infinite`,
	zIndex: 1,

	[theme.breakpoints.down("sm")]: {
		width: "60px",
		height: "60px",
	},
}));

export const HeaderDiv = styled("div")(({ theme }) => ({
	textAlign: "center",
	marginBottom: "40px",
	position: "relative",
	zIndex: 2,
}));

export const AnimatedBadge = styled("div")(({ theme }) => ({
	display: "inline-block",
	padding: "10px 30px",
	backgroundColor: colors.mainSecondary,
	color: "#ffffff",
	borderRadius: "50px",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.875rem",
	fontWeight: 700,
	textTransform: "uppercase",
	letterSpacing: "1px",
	marginBottom: "20px",
	animation: `${badgePulse} 2s ease-in-out infinite`,
	boxShadow: "0 4px 20px rgba(30, 193, 149, 0.4)",

	[theme.breakpoints.down("sm")]: {
		fontSize: "0.75rem",
		padding: "8px 20px",
	},
}));

export const MainTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(2rem, 4vw, 3.5rem)",
	fontWeight: 800,
	color: "#ffffff",
	marginBottom: "20px",
	lineHeight: 1.2,
	letterSpacing: "-0.02em",

	[theme.breakpoints.down("sm")]: {
		fontSize: "clamp(1.75rem, 5vw, 2.5rem)",
	},
}));

export const HighlightText = styled("span")({
	background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
	WebkitBackgroundClip: "text",
	WebkitTextFillColor: "transparent",
	backgroundClip: "text",
	position: "relative",
	display: "inline-block",
	animation: `${shimmer} 3s ease-in-out infinite`,
	backgroundSize: "200% auto",
});

export const StyledTypography = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(2rem, 4vw, 3rem)",
	fontWeight: 700,
	color: "#ffffff",
	marginBottom: "20px",

	[theme.breakpoints.down("sm")]: {
		fontSize: "clamp(1.5rem, 5vw, 2rem)",
	},
}));

export const SectionSubtitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1rem, 2vw, 1.25rem)",
	fontWeight: 400,
	color: "rgba(255, 255, 255, 0.85)",
	maxWidth: "800px",
	margin: "0 auto",
	lineHeight: 1.8,

	[theme.breakpoints.down("sm")]: {
		fontSize: "clamp(0.9rem, 2vw, 1.1rem)",
	},
}));

export const ContentWrapper = styled("div")(({ theme }) => ({
	position: "relative",
	zIndex: 2,
}));

export const FeatureCard = styled("div")(({ theme }) => ({
	backgroundColor: "rgba(255, 255, 255, 0.05)",
	backdropFilter: "blur(10px)",
	borderRadius: "20px",
	padding: "35px 25px",
	textAlign: "center",
	transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
	border: "1px solid rgba(255, 255, 255, 0.1)",
	position: "relative",
	overflow: "hidden",
	animation: `${float} 6s ease-in-out infinite`,

	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "3px",
		background: `linear-gradient(90deg, transparent, ${colors.mainSecondary}, transparent)`,
		transform: "translateX(-100%)",
		transition: "transform 0.6s ease",
	},

	"&:hover": {
		backgroundColor: "rgba(255, 255, 255, 0.08)",
		transform: "translateY(-10px)",
		boxShadow: `0 20px 40px rgba(30, 193, 149, 0.3)`,
		border: `1px solid ${colors.mainSecondary}`,

		"&::before": {
			transform: "translateX(100%)",
		},

		"& > div:first-of-type": {
			animation: `${pulse} 1s ease-in-out`,
			background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
		},
	},

	[theme.breakpoints.down("sm")]: {
		padding: "30px 20px",
	},
}));

export const InsuranceIconCircle = styled("div")(({ theme }) => ({
	width: "90px",
	height: "90px",
	borderRadius: "50%",
	background: `linear-gradient(135deg, rgba(30, 193, 149, 0.2) 0%, rgba(38, 227, 181, 0.2) 100%)`,
	border: `2px solid ${colors.mainSecondary}`,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 25px auto",
	transition: "all 0.3s ease",
	animation: `${glow} 3s ease-in-out infinite`,

	[theme.breakpoints.down("sm")]: {
		width: "75px",
		height: "75px",
	},
}));

export const IconWrapper = styled("div")({
	color: colors.mainSecondary,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	"& svg": {
		fontSize: "45px",
	},
});

export const FeatureTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
	fontWeight: 700,
	color: "#ffffff",
	marginBottom: "15px",
	letterSpacing: "0.5px",

	[theme.breakpoints.down("sm")]: {
		fontSize: "1.1rem",
	},
}));

export const FeatureDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(0.9rem, 1.2vw, 1rem)",
	fontWeight: 400,
	color: "rgba(255, 255, 255, 0.75)",
	lineHeight: 1.7,

	[theme.breakpoints.down("sm")]: {
		fontSize: "0.9rem",
	},
}));
